import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import {
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  BellIcon,
  UserGroupIcon,
  UserIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  departmentName?: string;
}

interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  type: 'email' | 'sms' | 'push';
}

interface NotificationStats {
  totalSent: number;
  successful: number;
  failed: number;
  pending: number;
}

const Messaging: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    totalSent: 0,
    successful: 0,
    failed: 0,
    pending: 0,
  });

  // Message form state
  const [messageType, setMessageType] = useState<'email' | 'sms' | 'push'>('email');
  const [recipientType, setRecipientType] = useState<'individual' | 'group'>('individual');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Bilingual drafting: many residents only read isiZulu, but every
  // broadcast composed here today is English-only. This drafts a
  // translation for the admin to review/edit — it never sends anything by
  // itself and never overrides what the admin decides to send.
  const [zuDraft, setZuDraft] = useState('');
  const [translating, setTranslating] = useState(false);
  const [includeZuDraft, setIncludeZuDraft] = useState(false);
  const [translateError, setTranslateError] = useState('');
  const [translationInteractionId, setTranslationInteractionId] = useState<string | null>(null);
  const [originalZuDraft, setOriginalZuDraft] = useState('');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Load users and templates on component mount
  useEffect(() => {
    loadUsers();
    loadTemplates();
    loadNotificationStats();
  }, []);

    const loadUsers = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      // Force fresh data by adding timestamp to prevent caching
      const response = await apiService.get('/users', {
        params: { _t: Date.now() }
      });
      
      console.log('Users API response:', response.data);
      
      console.log('Full API response structure:', response.data);
      
      // Check different possible response structures
      let usersData = null;
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (response.data.success && Array.isArray(response.data)) {
        usersData = response.data;
      } else if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      }
      
      if (usersData && usersData.length > 0) {
        console.log(`Loaded ${usersData.length} users from API`);
        console.log('First user example:', usersData[0]);
        
        // Transform the data to match our User interface
        const transformedUsers = usersData.map((user: any) => {
          console.log('Processing user:', user);
          
          // Handle different possible field names
          const transformedUser = {
            id: user.id || user.user_id || user.userId || '',
            email: user.email || user.email_address || '',
            firstName: user.first_name || user.firstName || user.firstname || user.given_name || '',
            lastName: user.last_name || user.lastName || user.lastname || user.family_name || '',
            role: user.role || user.user_role || user.role_name || 'resident',
            phone: user.phone || user.phone_number || user.mobile || user.contact_number || '',
            departmentName: user.department_name || user.departmentName || user.department || user.dept_name || ''
          };
          
          console.log('Transformed user:', transformedUser);
          return transformedUser;
        });
        
        console.log('Transformed users:', transformedUsers);
        setUsers(transformedUsers);
      } else {
        console.log('API returned no users or invalid structure');
        console.log('Response data:', response.data);
        setErrorMessage('No users found in the system. Please check your user database.');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setErrorMessage('Failed to load users. Please check your connection and try again.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await apiService.get('/push-notifications/templates');
      console.log('Templates API response:', response.data);
      if (response.success && response.data && Array.isArray(response.data)) {
        setTemplates(response.data);
      } else {
        console.log('No templates available from API');
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    }
  };

  const loadNotificationStats = async () => {
    try {
      const response = await apiService.get('/push-notifications/stats');
      console.log('Stats API response:', response.data);
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        // Use default stats if none available
        setStats({
          totalSent: 0,
          successful: 0,
          failed: 0,
          pending: 0
        });
      }
    } catch (error) {
      console.error('Error loading notification stats:', error);
      // Use default stats on error
      setStats({
        totalSent: 0,
        successful: 0,
        failed: 0,
        pending: 0
      });
    }
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleRoleSelection = (role: string) => {
    setSelectedRole(role);
    setSelectedDepartment(''); // Reset department when role changes
  };

  const handleTemplateSelection = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessageContent(template.content);
      setMessageType(template.type);
    }
    setSelectedTemplate(templateId);
  };

  const getRecipientCount = () => {
    if (recipientType === 'individual') {
      return selectedUsers.length;
    } else {
      if (selectedRole && selectedDepartment) {
        return users.filter(u => u.role === selectedRole && u.departmentName === selectedDepartment).length;
      } else if (selectedRole) {
        return users.filter(u => u.role === selectedRole).length;
      }
      return 0;
    }
  };

  const getRecipientList = () => {
    if (recipientType === 'individual') {
      return users.filter(u => selectedUsers.includes(u.id));
    } else {
      if (selectedRole && selectedDepartment) {
        return users.filter(u => u.role === selectedRole && u.departmentName === selectedDepartment);
      } else if (selectedRole) {
        return users.filter(u => u.role === selectedRole);
      }
      return [];
    }
  };

  const validateForm = () => {
    if (!messageContent.trim()) {
      setErrorMessage('Message content is required');
      return false;
    }

    if (recipientType === 'individual' && selectedUsers.length === 0) {
      setErrorMessage('Please select at least one recipient');
      return false;
    }

    if (recipientType === 'group' && !selectedRole) {
      setErrorMessage('Please select a role for group messaging');
      return false;
    }

    if (messageType === 'email' && !messageSubject.trim()) {
      setErrorMessage('Subject is required for email messages');
      return false;
    }

    return true;
  };

  const draftZuTranslation = async () => {
    if (!messageContent.trim()) return;
    setTranslating(true);
    setTranslateError('');
    try {
      const response = await apiService.post('/platform/messaging/translate', {
        text: messageContent,
        targetLanguage: 'isiZulu',
      });
      const translation = response.data?.data?.translation;
      if (translation) {
        setZuDraft(translation);
        setOriginalZuDraft(translation);
        setTranslationInteractionId(response.data?.data?.interactionId || null);
        setIncludeZuDraft(true);
      } else {
        setTranslateError('Translation is unavailable right now — draft manually or try again.');
      }
    } catch (error) {
      console.error('Error drafting isiZulu translation:', error);
      setTranslateError('Translation is unavailable right now — draft manually or try again.');
    } finally {
      setTranslating(false);
    }
  };

  const sendMessage = async () => {
    if (!validateForm()) return;

    try {
      setIsSending(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (translationInteractionId) {
        const decision = !includeZuDraft ? 'rejected'
          : zuDraft.trim() === originalZuDraft.trim() ? 'accepted' : 'modified';
        await apiService.post('/ai-operations/feedback', {
          interactionId: translationInteractionId,
          decision,
          suggestedValue: originalZuDraft,
          finalValue: includeZuDraft ? zuDraft : '',
        }).catch(() => undefined);
        await apiService.post('/pilot/events', {
          eventType: `translation_${decision}`,
          workflow: 'municipal_messaging',
          variant: 'assisted',
          dimensions: { source: 'admin_portal' },
        }).catch(() => undefined);
      }

      const recipients = getRecipientList();

      // Admin reviewed and approved this before sending — the translation
      // is never sent automatically, only when explicitly included here.
      const finalBody = includeZuDraft && zuDraft.trim()
        ? `${messageContent}\n\n— isiZulu —\n${zuDraft.trim()}`
        : messageContent;

      // Extract user IDs for push notifications endpoint
      const userIds = recipients.map(u => u.id);

      // Prepare payload for push notifications endpoint
      const pushNotificationPayload = {
        userIds,
        title: messageType === 'email' ? messageSubject : `New ${messageType} message`,
        body: finalBody,
        data: {
          type: messageType,
          subject: messageSubject,
          content: finalBody,
          sentBy: user?.id,
          sentAt: new Date().toISOString(),
          recipientCount: recipients.length,
        },
        priority: 'high' // High priority for messaging (like WhatsApp)
      };

      let response;
      if (messageType === 'email') {
        // Try the email endpoint, fallback to push notifications if it doesn't exist
        try {
          const emailPayload = {
            recipients: recipients.map(u => ({
              id: u.id,
              email: u.email,
              firstName: u.firstName,
              lastName: u.lastName,
              phone: u.phone,
            })),
            subject: messageSubject,
            content: finalBody,
            metadata: {
              sentBy: user?.id,
              sentAt: new Date().toISOString(),
              recipientCount: recipients.length,
            },
          };
          
          response = await apiService.post('/test/send-email-bulk', emailPayload);
        } catch (emailError: any) {
          if (emailError.response?.status === 404) {
            console.log('Email endpoint not found, using push notifications instead');
            // Fallback to push notifications for email
            response = await apiService.post('/push-notifications/send-bulk', pushNotificationPayload);
          } else {
            throw emailError;
          }
        }
      } else if (messageType === 'sms') {
        response = await apiService.post('/push-notifications/send-bulk', pushNotificationPayload);
      } else {
        response = await apiService.post('/push-notifications/send-bulk', pushNotificationPayload);
      }

      if (response.success) {
        console.log('Message sent successfully:', response);
        setSuccessMessage(`Message sent successfully to ${recipients.length} recipient(s)`);
        resetForm();
        loadNotificationStats(); // Refresh stats
      } else {
        console.log('Message failed to send:', response);
        setErrorMessage(response.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      console.error('Error response:', error.response);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setSelectedUsers([]);
    setSelectedRole('');
    setSelectedDepartment('');
    setMessageSubject('');
    setMessageContent('');
    setSelectedTemplate('');
    setShowPreview(false);
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EnvelopeIcon className="w-5 h-5" />;
      case 'sms':
        return <DevicePhoneMobileIcon className="w-5 h-5" />;
      case 'push':
        return <BellIcon className="w-5 h-5" />;
      default:
        return <EnvelopeIcon className="w-5 h-5" />;
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'sms':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'push':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const departments = Array.from(new Set(users.map(u => u.departmentName).filter(Boolean)));
  const roles = Array.from(new Set(users.map(u => u.role)));

  return (
    <div>
      <PageHeader
        title="Messaging Center"
        subtitle="Send SMS, email, or push notifications to technicians, residents, or managers"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Sent" value={stats.totalSent} icon={PaperAirplaneIcon} tone="blue" />
        <StatCard label="Successful" value={stats.successful} icon={CheckCircleIcon} tone="emerald" />
        <StatCard label="Failed" value={stats.failed} icon={ExclamationTriangleIcon} tone="red" />
        <StatCard label="Pending" value={stats.pending} icon={BellIcon} tone="amber" />
      </div>

      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-amber-800">Debug Information</h3>
            <button
              onClick={() => {
                loadUsers();
                loadTemplates();
                loadNotificationStats();
              }}
              className="px-3 py-1 text-xs bg-amber-100 text-amber-800 rounded-sm border border-amber-300 hover:bg-amber-200"
            >
              Refresh Data
            </button>
          </div>
          <div className="text-xs text-amber-700 space-y-1">
            <p>Users loaded: {users.length}</p>
            <p>Templates loaded: {templates.length}</p>
            <p>Stats loaded: {stats.totalSent > 0 || stats.successful > 0 || stats.failed > 0 ? 'Yes' : 'No'}</p>
            <p>Selected users: {selectedUsers.length}</p>
            <p>Selected role: {selectedRole || 'None'}</p>
            <p>Selected department: {selectedDepartment || 'None'}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Message Form */}
          <div className="lg:col-span-2">
            <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Compose Message</h2>
              </div>

              <div className="p-6 space-y-6">
                {/* Message Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Message Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { type: 'email', label: 'Email', icon: EnvelopeIcon },
                      { type: 'sms', label: 'SMS', icon: DevicePhoneMobileIcon },
                      { type: 'push', label: 'Push Notification', icon: BellIcon },
                    ].map(({ type, label, icon: Icon }) => (
                      <button
                        key={type}
                        onClick={() => setMessageType(type as any)}
                        className={`p-4 border rounded-lg text-center transition-colors ${
                          messageType === type
                            ? 'border-primary-600 bg-primary-50 text-primary-800'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <Icon className="w-6 h-6 mx-auto mb-2" />
                        <span className="text-sm font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recipient Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Recipients
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => setRecipientType('individual')}
                      className={`p-3 border rounded-lg text-center transition-colors ${
                        recipientType === 'individual'
                          ? 'border-primary-600 bg-primary-50 text-primary-800'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <UserIcon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Individual Users</span>
                    </button>
                    <button
                      onClick={() => setRecipientType('group')}
                      className={`p-3 border rounded-lg text-center transition-colors ${
                        recipientType === 'group'
                          ? 'border-primary-600 bg-primary-50 text-primary-800'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <UserGroupIcon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Groups</span>
                    </button>
                  </div>

                  {recipientType === 'individual' ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Select recipients ({selectedUsers.length} selected)
                        </span>
                        <button
                          onClick={() => setSelectedUsers([])}
                          className="text-sm text-primary-700 hover:text-primary-800"
                        >
                          Clear All
                        </button>
                      </div>
                      
                      {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading users...</span>
                        </div>
                      ) : users.length === 0 ? (
                        <div className="text-center p-8">
                          {errorMessage ? (
                            <div className="space-y-3">
                              <div className="text-red-600">
                                <ExclamationTriangleIcon className="w-8 h-8 mx-auto mb-2" />
                                <p className="font-medium">Failed to load users</p>
                                <p className="text-sm">{errorMessage}</p>
                              </div>
                              <button
                                onClick={loadUsers}
                                className="px-4 py-2 bg-primary-700 text-white rounded-sm text-sm hover:bg-primary-800"
                              >
                                Try Again
                              </button>
                            </div>
                          ) : (
                            <div className="text-gray-500 dark:text-gray-400">
                              <p>No users available</p>
                              <p className="text-sm">Please check your user database or contact support</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2">
                          {users.map((user) => (
                            <label key={user.id} className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleUserSelection(user.id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {user.email} • {user.role}
                                  {user.departmentName && ` • ${user.departmentName}`}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Role
                        </label>
                        <select
                          value={selectedRole}
                          onChange={(e) => handleRoleSelection(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">Select a role</option>
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedRole && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Department (Optional)
                          </label>
                          <select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">All departments</option>
                            {departments.map((dept) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {getRecipientCount() > 0 && (
                    <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-sm">
                      <p className="text-sm text-primary-800">
                        Message will be sent to <strong>{getRecipientCount()}</strong> recipient(s)
                      </p>
                    </div>
                  )}
                </div>

                {/* Message Templates */}
                {templates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message Templates (Optional)
                    </label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => handleTemplateSelection(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select a template</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Message Subject (Email only) */}
                {messageType === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      placeholder="Enter email subject"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {/* Message Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message Content
                  </label>
                  <textarea
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder={`Enter your ${messageType} message...`}
                    rows={6}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {messageContent.length} characters
                    </span>
                    {messageType === 'sms' && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {Math.ceil(messageContent.length / 160)} SMS message(s)
                      </span>
                    )}
                  </div>
                </div>

                {/* Bilingual drafting — draft only, admin reviews/edits before
                    anything is sent, and the translation is never included
                    unless explicitly checked below. */}
                <div className="rounded-sm border border-accent-500 bg-accent-50 p-4 dark:border-accent-500 dark:bg-gray-800">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-primary-900 dark:text-gray-100 flex items-center gap-2">
                      <SparklesIcon className="h-4 w-4 text-accent-600" />
                      isiZulu draft
                    </h3>
                    <button
                      onClick={draftZuTranslation}
                      disabled={translating || !messageContent.trim()}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-primary-700 text-primary-700 dark:text-gray-100 dark:border-gray-500 hover:bg-primary-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {translating ? 'Drafting…' : zuDraft ? 'Redraft' : 'Draft isiZulu version'}
                    </button>
                  </div>
                  {translateError && (
                    <p className="text-xs text-red-600 mb-2">{translateError}</p>
                  )}
                  {zuDraft && (
                    <>
                      <textarea
                        value={zuDraft}
                        onChange={(e) => setZuDraft(e.target.value)}
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      />
                      <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={includeZuDraft}
                          onChange={(e) => setIncludeZuDraft(e.target.checked)}
                        />
                        Include this isiZulu translation when sending
                      </label>
                    </>
                  )}
                  {!zuDraft && !translateError && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Draft an isiZulu version of the message above for residents who read isiZulu — you review and edit it before it's ever included.
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                    >
                      {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                    <button
                      onClick={resetForm}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>

                  <button
                    onClick={sendMessage}
                    disabled={isSending || getRecipientCount() === 0}
                    className="px-6 py-2 bg-primary-700 text-white rounded-sm font-medium hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-4 h-4" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Preview and Recipients Panel */}
          <div className="space-y-6">
            {/* Message Preview */}
            {showPreview && (
              <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Message Preview</h3>
                </div>
                <div className="p-6">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getMessageTypeColor(messageType)}`}>
                    {getMessageTypeIcon(messageType)}
                    <span className="ml-2 capitalize">{messageType}</span>
                  </div>
                  
                  {messageType === 'email' && messageSubject && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Subject:</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{messageSubject}</p>
                    </div>
                  )}
                  
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Content:</p>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{messageContent || 'No content yet'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recipients List */}
            {getRecipientCount() > 0 && (
              <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Recipients ({getRecipientCount()})
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {getRecipientList().map((user) => (
                      <div key={user.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {user.role}
                            {user.departmentName && ` • ${user.departmentName}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md">
            <div className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-3 text-green-600 hover:text-green-800"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage('')}
                className="ml-3 text-red-600 hover:text-red-800"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default Messaging;
